import z from 'zod';

import { Image } from '@@api/utils/schemas/schemas';
import { getElementTypes } from '@@plate/utils/getElementTypes';

import { ParagraphCodec, type ParagraphElement, PlateParagraph, UnityParagraph } from './Paragraph';
import { PlateTextElements } from './Text';
import { CURRENT_VERSION, ElementId, emptyChildren, UnityElementId, Version } from '../commons';
import { ELEMENT_TYPES } from '../constants';

const UnityDynamicTeaserImage = z
    .object({
        ...Image.shape,
        caption: z
            .object({
                richTextFormat: z.array(UnityParagraph),
            })
            .nullish(),
    })
    .nullish();

const PlateDynamicTeaserImage = z
    .object({
        ...Image.shape,
        // Caption can be PlateParagraph[] (from decode) or any (from ImageField with CaptionEditorField)
        // ImageField uses the API Image schema where caption is z.any(), so we need the union to accept both
        caption: z.union([z.array(PlateParagraph), z.any()]).nullish(),
    })
    .nullish();

export const UnityDynamicTeaser = z.object({
    type: z.literal(ELEMENT_TYPES.DYNAMIC_TEASER),
    elementId: UnityElementId,
    templateElement: z.boolean().optional(),
    metadataIds: z.array(z.number()).nullable(),
    image: UnityDynamicTeaserImage,
    version: Version,
    title: UnityParagraph.nullable(),
});
export type UnityDynamicTeaser = z.infer<typeof UnityDynamicTeaser>;

export const PlateDynamicTeaserTitle = z.object({
    type: z.literal(ELEMENT_TYPES.DYNAMIC_TEASER_TITLE),
    id: ElementId,
    data: z
        .object({
            templateElement: z.boolean().optional(),
        })
        .optional(),
    children: PlateTextElements,
});
export type DynamicTeaserTitleElement = z.infer<typeof PlateDynamicTeaserTitle>;

export const DYNAMIC_TEASER_TITLE_ELEMENT_TYPES = getElementTypes([PlateDynamicTeaserTitle]);

export const PlateDynamicTeaser = z.object({
    type: z.literal(ELEMENT_TYPES.DYNAMIC_TEASER),
    id: ElementId,
    data: z.object({
        templateElement: z.boolean().optional(),
        metadataIds: z.array(z.number()),
        image: PlateDynamicTeaserImage,
    }),
    children: z.tuple([PlateDynamicTeaserTitle]),
});
export type DynamicTeaserElement = z.infer<typeof PlateDynamicTeaser>;

export const DynamicTeaserCodec = z.codec(UnityDynamicTeaser, PlateDynamicTeaser, {
    decode: (value) => {
        const decodecParagraph: ParagraphElement = value.title
            ? ParagraphCodec.decode(value.title)
            : { type: ELEMENT_TYPES.PARAGRAPH, id: crypto.randomUUID(), children: emptyChildren };

        const title: DynamicTeaserTitleElement = {
            ...decodecParagraph,
            type: ELEMENT_TYPES.DYNAMIC_TEASER_TITLE,
        };

        const image = value.image
            ? {
                  ...value.image,
                  caption: value.image.caption?.richTextFormat
                      ? value.image.caption.richTextFormat.map((paragraph) =>
                            ParagraphCodec.decode(paragraph),
                        )
                      : undefined,
              }
            : undefined;

        return {
            type: value.type,
            id: value.elementId ?? crypto.randomUUID(),
            data: {
                metadataIds: value.metadataIds || [],
                image,
                ...(value.templateElement && { templateElement: value.templateElement }),
            },
            children: [title] satisfies [DynamicTeaserTitleElement],
        };
    },
    encode: (value) => {
        const paragraph: ParagraphElement = {
            ...value.children[0],
            type: ELEMENT_TYPES.PARAGRAPH,
        };

        const title = ParagraphCodec.encode(paragraph);

        const image = value.data.image
            ? {
                  ...value.data.image,
                  caption:
                      value.data.image.caption && Array.isArray(value.data.image.caption)
                          ? {
                                richTextFormat: (
                                    value.data.image.caption as ParagraphElement[]
                                ).map((paragraph) =>
                                    ParagraphCodec.encode({
                                        ...paragraph,
                                        id: paragraph.id ?? crypto.randomUUID(),
                                    }),
                                ),
                            }
                          : undefined,
              }
            : undefined;

        return {
            type: value.type,
            elementId: value.id,
            version: CURRENT_VERSION,
            title,
            metadataIds: value.data.metadataIds,
            image,
            ...(value.data.templateElement && { templateElement: value.data.templateElement }),
        };
    },
});
